import { HttpErrorResponse } from "@angular/common/http";
import { Component, effect, EventEmitter, Output, OnInit, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, Validators, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { map, catchError, throwError, finalize } from "rxjs";
import { IBreadcrumbItem } from "../../../main/layout/tool-bar/model/breadcrumb.model";
import { ICreateFolderRequest } from "../models/create-folder-request.model";
import { AppSettingsService, SpinnerService, StudyCodeHelperService } from "../../../core";
import { MessageService } from "../../../main";
import { BucketItemService } from "../service/bucket-item.service";
import { BreadcrumbDataService } from "../../../main/layout/tool-bar/breadcrumb-service/breadcrumb-data.service";
import {
  BucketTypeConstants,
  CommonConsants,
  ErrorConstants,
  LoadTemplateModeConstants
} from "../constants/component-constants";
import { IBucketItemRequest } from "../models/bucket-item-request.model";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatFormField, MatLabel, MatInput, MatError } from "@angular/material/input";

@Component({
    selector: "app-create-folder",
    templateUrl: "./create-folder.component.html",
    styleUrls: ["./create-folder.component.scss"],
    imports: [FormsModule, ReactiveFormsModule, MatIconButton, MatIcon, MatFormField, MatLabel, MatInput, MatError, MatButton]
})
export class CreateFolderComponent implements OnInit {
  private bucketItemsService = inject(BucketItemService);
  private appSettingService = inject(AppSettingsService);
  private messageService = inject(MessageService);
  private fb = inject(FormBuilder);
  private breadcrumbDataService = inject(BreadcrumbDataService);
  private spinnerService = inject(SpinnerService);
  private studyCodeHelperService = inject(StudyCodeHelperService);
  dialogData = inject<{
    isFromLoadTemplate: boolean;
    templateId: string;
    bucketId: string;
    directoryName: string;
    templateName: string;
    mode: string;
    dialogTitle: string;
}>(MAT_DIALOG_DATA, { optional: true });
  private dialogRef = inject<MatDialogRef<CreateFolderComponent>>(MatDialogRef, { optional: true });

  @Output() getAllBucketItemsRequest = new EventEmitter<IBucketItemRequest>();

  public form = this.fb.group({
    folderName: new FormControl("", [
      Validators.maxLength(CommonConsants.FILES_AND_FOLDERS_MAX_LENGTH),
      Validators.required,
      Validators.pattern('^[^/:*?\\\\"<>|]+$'),
    ]),
  });
  public requestData: ICreateFolderRequest = null;
  public currentBreadcrumbItem: IBreadcrumbItem = null;
  public submitButtonClicked = false;
  public isFromLoadTemplate = false;
  public isStudyPackageFolder = false;
  public isPackageFolder = false;

  private readonly _breadcrumb = toSignal(this.breadcrumbDataService.getCurrentBreadCrumbItem());

  constructor() {
    const dialogData = this.dialogData;

    // Check if this component was opened from load template
    this.isFromLoadTemplate = dialogData?.isFromLoadTemplate || false;
    this.isStudyPackageFolder = dialogData?.mode === LoadTemplateModeConstants.STUDY;
    this.isPackageFolder = dialogData?.mode === LoadTemplateModeConstants.PACKAGE;
    effect(() => {
      const item = this._breadcrumb();
      if (item != null) this.currentBreadcrumbItem = item as IBreadcrumbItem;
    });
  }

  ngOnInit(): void {
    // Set default folder name for template loading (non-study folders)
    if (this.isFromLoadTemplate && !this.isStudyPackageFolder) {
      const defaultName = this.dialogData?.templateName?.trim();

      if (defaultName && defaultName.length > 0) {
        this.form.controls.folderName.setValue(defaultName);
        this.form.controls.folderName.markAsPristine();
        this.form.controls.folderName.markAsUntouched();
      }
    }

    this.form.controls.folderName.valueChanges.subscribe((name) => {
      this.form.controls.folderName.markAsTouched();
      if (
        this.form.controls.folderName.value?.length > 0 &&
        this.currentBreadcrumbItem.DirectoryName.split("/").length === 2
      ) {
        if (this.currentBreadcrumbItem.BucketType ===
          BucketTypeConstants.STUDYDATACOLLECTION && !this.studyCodeHelperService.isValidStudyCode(name ?? '')) {
          this.form.controls.folderName.setErrors({
            inValidStudyPackageFolderName: true,
          });
        }
        if (this.currentBreadcrumbItem.BucketType ===
          BucketTypeConstants.FACILITYDATACOLLECTION && this.studyCodeHelperService.isValidStudyCode(name ?? '')) {
          this.form.controls.folderName.setErrors({
            inValidFacilityPackageFolderName: true,
          });
        }     
       }
    });
  }

  createFolderSubmit() {
    if (!this.form.valid) return;
    let folderName = this.form.controls.folderName.value;

    if (this.isFromLoadTemplate) {
      if (this.isStudyPackageFolder) {
        if (!this.studyCodeHelperService.isValidStudyCode(folderName ?? '')) {
          this.messageService.alert(
            ErrorConstants.INVALID_STUDY_CODE_MESSAGE,
            this.appSettingService.toastrDisplayTime
          );
          return;
        }
      } else {
        if (!folderName || folderName.trim().length === 0) {
          this.messageService.alert(
            ErrorConstants.INVALID_FOLDER_NAME_MESSAGE,
            this.appSettingService.toastrDisplayTime
          );
          return;
        }

        while (folderName.endsWith('.')) {
          folderName = folderName.slice(0, -1);
        }
      }
      
      // Check if user is offline
      if (!navigator.onLine) {
        this.messageService.alert(
          'You are offline. Cannot load template at this time.',
          this.appSettingService.toastrDisplayTime
        );
        return;
      }
      
      let isSubFolder = this.dialogData.mode === LoadTemplateModeConstants.SUBFOLDER;
      this.spinnerService.show(true);
      this.bucketItemsService.LoadTemplate({
        templateId: this.dialogData.templateId,
        bucketId: this.dialogData.bucketId,
        isBucketItem: isSubFolder,
        dirName: this.dialogData.directoryName,
        studyFolderName: folderName,
        bucketType: this.currentBreadcrumbItem.BucketType,
        buCode: this.currentBreadcrumbItem.BUCode
      })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.error.detail != undefined) {
              this.messageService.alert(
                error.error.detail,
                this.appSettingService.toastrDisplayTime
              );
            } else {
              this.messageService.alert(
                "Failed to load template",
                this.appSettingService.toastrDisplayTime
              );
            }
            return throwError(() => new Error(error.message));
          }),
          finalize(() => {
            this.spinnerService.show(false);
          })
        )
        .subscribe(() => {
          this.messageService.success(
            this.sucessMessage(isSubFolder),
            this.appSettingService.toastrDisplayTime
          );
          const bucketItemRequest: IBucketItemRequest = {
            BucketId: this.dialogData.bucketId,
            BucketType: this.currentBreadcrumbItem.BucketType,
            BUCode: this.currentBreadcrumbItem.BUCode,
            EleName: this.currentBreadcrumbItem.EleName ?? '',
            DirectoryName: this.dialogData.directoryName,
            PackageId: undefined
          };
          this.getAllBucketItemsRequest.emit(bucketItemRequest);
          this.dialogRef.close();
        });
      return;
    }

    while(folderName.endsWith(".")){
      folderName = folderName.slice(0,-1)
    }
    this.requestData = {
      BucketId: this.currentBreadcrumbItem.BucketId,
      ParentId: this.currentBreadcrumbItem.ParentId,
      FolderName: folderName,
      BucketType: this.currentBreadcrumbItem.BucketType,
      BUCode: this.currentBreadcrumbItem.BUCode,
    };
    this.submitButtonClicked = true;
    this.spinnerService.show(true);
    this.bucketItemsService
      .CreateFolder(this.requestData)
      .pipe(
        map((res: boolean) => {
          if(res){
            this.submitButtonClicked = false;
            this.messageService.success(
              CommonConsants.FOLDER_CREATION_SUCCESS,
              this.appSettingService.toastrDisplayTime
            );
            this.form.reset();
            this.getAllBucketItemsRequest.emit(this.currentBreadcrumbItem);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.submitButtonClicked = false;
          if (error.error.detail) {
            this.messageService.alert(
              error.error.detail,
              this.appSettingService.toastrDisplayTime
            );
          } else {
            this.messageService.alert(
              CommonConsants.FOLDER_CREATION_FAILED,
              this.appSettingService.toastrDisplayTime
            );
          }
          return throwError(() => new Error(error.message));
        }),
        finalize(()=>{
          this.spinnerService.show(false);
        })
      )
      .subscribe();
  }

  closeDialog(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  sucessMessage(isSubfolder: boolean): string {
    if (isSubfolder)
    {
      return CommonConsants.LOAD_SUB_FOLDER_TEMPLATE_SUCCESS;
    }
    return CommonConsants.LOAD_PACKAGE_FOLDER_TEMPLATE_SUCCESS;
  }
}